/**
 * Route Generator - Dynamically creates routes from moduleRegistry
 * 
 * SOLID Principles:
 * - Single Responsibility: Generates React Router routes
 * - Open/Closed: Add modules to registry, routes generated automatically
 * - Liskov Substitution: All routes follow same protection pattern
 */

import { Route } from 'react-router-dom'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import ModuleFlagGuard from '../components/auth/ModuleFlagGuard'
import { MODULE_REGISTRY, getAllRoutesForModule, filterModulesByMarketplace } from './moduleRegistry'
import { ROUTE_COMPONENT_MAP } from './routeComponentMap'

/**
 * Loading fallback for lazy-loaded components
 */
const LoadingFallback = () => {
  const { t } = useTranslation('common')
  return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5A00]"></div>
        <p className="text-gray-500 text-sm">{t('loading')}</p>
      </div>
    </div>
  )
}

/**
 * Generate routes for all modules in the registry
 * Returns an array of Route elements
 * @param isMarketplaceEnabled - Optional function to filter modules by marketplace
 */
export const generateModuleRoutes = (isMarketplaceEnabled?: (marketplace: string) => boolean) => {
  const routes: JSX.Element[] = []

  // Filter modules by marketplace if filter function provided
  const modulesToRender = isMarketplaceEnabled 
    ? filterModulesByMarketplace(MODULE_REGISTRY, isMarketplaceEnabled)
    : MODULE_REGISTRY

  modulesToRender.forEach(module => {
    const Component = ROUTE_COMPONENT_MAP[module.id]
    
    if (!Component) {
      console.warn(`⚠️ No component mapped for module: ${module.id} (route: ${module.route})`)
      return
    }
  
    // Get all routes (main + legacy)
    const allRoutes = getAllRoutesForModule(module)

    allRoutes.forEach((routePath, index) => {
      const isLegacyRoute = index > 0
      const routeKey = isLegacyRoute ? `${module.id}-legacy-${index}` : module.id
      // Nested Ads workspace (S5b): splat so /allegro/ads/campaigns/... hits the same shell.
      const resolvedPath =
        !isLegacyRoute && module.id === 'allegro_ads' ? `${routePath}/*` : routePath

      // Create element with Suspense for lazy loading
      const page = (
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      )
      const element = module.featureFlag ? (
        <ModuleFlagGuard module={module}>{page}</ModuleFlagGuard>
      ) : (
        page
      )

      // Core modules and alwaysVisibleInNav don't require permission check on route
      if (module.isCore || module.requirePermission === false || module.alwaysVisibleInNav) {
        routes.push(
          <Route 
            key={routeKey} 
            path={resolvedPath} 
            element={element}
          />
        )
      } else {
        // Protected route with permission check
        routes.push(
          <Route
            key={routeKey}
            path={resolvedPath}
            element={
              <ProtectedRoute requireModule={module.id}>
                {element}
              </ProtectedRoute>
            }
          />
        )
      }
    })
  })

  return routes
}

/**
 * Special routes not in module registry (admin, profile, etc.)
 * @param featureFlags - Optional feature flag checker functions
 */
export const generateSpecialRoutes = (featureFlags?: {
  isAIConfigEnabled?: () => boolean
  isUsersAnalyticsEnabled?: () => boolean
  isAIUsageEnabled?: () => boolean
  isUserAIConfigEnabled?: () => boolean
}) => {
  const routes: JSX.Element[] = []
  
  // Admin credits route
  const AdminCreditsComponent = ROUTE_COMPONENT_MAP['admin_credits']
  if (AdminCreditsComponent) {
    routes.push(
      <Route
        key="admin-credits"
        path="/admin/credits"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminCreditsComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  const AdminReferralsComponent = ROUTE_COMPONENT_MAP['admin_referrals']
  if (AdminReferralsComponent) {
    routes.push(
      <Route
        key="admin-referrals"
        path="/admin/referrals"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminReferralsComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  const AdminAccountingComponent = ROUTE_COMPONENT_MAP['admin_accounting']
  if (AdminAccountingComponent) {
    routes.push(
      <Route
        key="admin-accounting"
        path="/admin/accounting"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminAccountingComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  const AdminSiteNoticeComponent = ROUTE_COMPONENT_MAP['admin_site_notice']
  if (AdminSiteNoticeComponent) {
    routes.push(
      <Route
        key="admin-site-notice"
        path="/admin/site-notice"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminSiteNoticeComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  const AdminAdsAgencyComponent = ROUTE_COMPONENT_MAP['admin_ads_agency']
  if (AdminAdsAgencyComponent) {
    routes.push(
      <Route
        key="admin-ads-agency"
        path="/admin/ads-agency"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminAdsAgencyComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  const AdminFeatureSurveysComponent = ROUTE_COMPONENT_MAP['admin_feature_surveys']
  if (AdminFeatureSurveysComponent) {
    routes.push(
      <Route
        key="admin-feature-surveys"
        path="/admin/feature-surveys"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminFeatureSurveysComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  const AdminOnboardingAnalyticsComponent = ROUTE_COMPONENT_MAP['admin_onboarding_analytics']
  if (AdminOnboardingAnalyticsComponent) {
    routes.push(
      <Route
        key="admin-onboarding-analytics"
        path="/admin/onboarding-analytics"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminOnboardingAnalyticsComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  const AdminGrafikomatFeedbackComponent = ROUTE_COMPONENT_MAP['admin_grafikomat_feedback']
  if (AdminGrafikomatFeedbackComponent) {
    routes.push(
      <Route
        key="admin-grafikomat-feedback"
        path="/admin/grafikomat-feedback"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminGrafikomatFeedbackComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }

  // Profile routes - always accessible
  const ProfileComponent = ROUTE_COMPONENT_MAP['profil']
  if (ProfileComponent) {
    routes.push(
      <Route 
        key="profile" 
        path="/profile" 
        element={
          <Suspense fallback={<LoadingFallback />}>
            <ProfileComponent />
          </Suspense>
        } 
      />
    )
  }
  
  // User AI Config route - conditional based on feature flag
  const AIConfigComponent = ROUTE_COMPONENT_MAP['ai_config']
  if (AIConfigComponent && (!featureFlags?.isUserAIConfigEnabled || featureFlags.isUserAIConfigEnabled())) {
    routes.push(
      <Route 
        key="ai-config" 
        path="/profile/ai-config" 
        element={
          <Suspense fallback={<LoadingFallback />}>
            <AIConfigComponent />
          </Suspense>
        } 
      />
    )
  }
  
  // Simple user management (for clients - simplified version)
  const SimpleUserManagementComponent = ROUTE_COMPONENT_MAP['simple_user_management']
  if (SimpleUserManagementComponent) {
    routes.push(
      <Route 
        key="simple-user-management"
        path="/admin/users-simple" 
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <SimpleUserManagementComponent />
            </Suspense>
          </ProtectedRoute>
        } 
      />
    )
  }
  
  // Admin routes - role-based protection (complex version - hidden for clients)
  const AdminUsersComponent = ROUTE_COMPONENT_MAP['admin_users']
  if (AdminUsersComponent) {
    routes.push(
      <Route 
        key="admin-users"
        path="/admin/users" 
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminUsersComponent />
            </Suspense>
          </ProtectedRoute>
        } 
      />
    )
  }
  
  const AdminAIPromptsComponent = ROUTE_COMPONENT_MAP['admin_ai_prompts']
  if (AdminAIPromptsComponent && (!featureFlags?.isAIConfigEnabled || featureFlags.isAIConfigEnabled())) {
    routes.push(
      <Route 
        key="admin-ai-prompts"
        path="/admin/ai-prompts" 
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminAIPromptsComponent />
            </Suspense>
          </ProtectedRoute>
        } 
      />
    )
  }

  const AdminOpisomatConfigComponent = ROUTE_COMPONENT_MAP['admin_opisomat_config']
  if (AdminOpisomatConfigComponent) {
    routes.push(
      <Route
        key="admin-opisomat-config"
        path="/admin/opisomat-config"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <AdminOpisomatConfigComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }
  
  // Team analytics - role-based
  const UsersAnalyticsComponent = ROUTE_COMPONENT_MAP['users_analytics']
  if (UsersAnalyticsComponent && (!featureFlags?.isUsersAnalyticsEnabled || featureFlags.isUsersAnalyticsEnabled())) {
    routes.push(
      <Route 
        key="users-analytics"
        path="/users-analytics" 
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <UsersAnalyticsComponent />
            </Suspense>
          </ProtectedRoute>
        } 
      />
    )
  }

  const GrafikomatPipelinePreviewComponent = ROUTE_COMPONENT_MAP['dev_grafikomat_pipeline_preview']
  if (GrafikomatPipelinePreviewComponent) {
    routes.push(
      <Route
        key="dev-grafikomat-pipeline-preview"
        path="/dev/grafikomat-pipeline-preview"
        element={
          <ProtectedRoute requireRole="admin">
            <Suspense fallback={<LoadingFallback />}>
              <GrafikomatPipelinePreviewComponent />
            </Suspense>
          </ProtectedRoute>
        }
      />
    )
  }
  
  return routes
}
